import { InMemoryWebStorage, UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'
import { inject, readonly, shallowRef, type App, type InjectionKey, type Ref } from 'vue'
import type { NavigationGuardWithThis, Router } from 'vue-router'

// @acme/auth: Authorization Code + PKCE against the Identity Service (OpenIddict). Tokens live
// only in memory (never localStorage); the PKCE state survives the redirect in sessionStorage.
// SSO across apps comes from the Identity Service session cookie, so a reload just goes through
// /connect/authorize again without asking for the password.

export interface AuthOptions {
  authority: string
  clientId: string
  scopes: string
  // Origin + base path of the app, e.g. http://localhost:5173/
  appUrl: string
  callbackPath?: string
  // Where the Identity sends the browser after logout (registered post-logout URI, e.g. the
  // access app's /entrar/saiu?app=assistente). Defaults to the app itself.
  postLogoutUrl?: string
  // White-label hosts (acme.aubaine.com.br): the Identity also answers on the page's own origin,
  // so the endpoints are taken from location.origin while the tokens keep the single public
  // issuer (`authority`). No discovery request: the Identity's discovery would advertise the
  // issuer host and send the login to another domain.
  sameOriginEndpoints?: boolean
}

export interface LoginOptions {
  // Tenant the user is trying to open: the Identity's login screen shows its brand.
  tenant?: string
  // The previous session ended (token expired / renewal failed): the login explains it.
  expired?: boolean
}

export interface AuthUser {
  subject: string
  name: string | undefined
  email: string | undefined
}

export interface Auth {
  user: Readonly<Ref<AuthUser | null>>
  isAuthenticated: () => boolean
  getAccessToken: () => Promise<string | null>
  login: (returnPath?: string, options?: LoginOptions) => Promise<void>
  // Completes the redirect sign-in and returns the in-app path to go back to.
  handleCallback: () => Promise<string>
  // Global logout: ends the Identity Service session (all apps).
  logout: () => Promise<void>
  install: (app: App) => void
  callbackPath: string
}

const AUTH_KEY: InjectionKey<Auth> = Symbol('acme-auth')

export function createAuth(options: AuthOptions): Auth {
  const appUrl = options.appUrl.endsWith('/') ? options.appUrl : `${options.appUrl}/`
  const callbackPath = options.callbackPath ?? 'auth/callback'

  const origin = window.location.origin
  const manager = new UserManager({
    authority: options.authority,
    ...(options.sameOriginEndpoints
      ? {
          metadata: {
            issuer: options.authority,
            authorization_endpoint: `${origin}/connect/authorize`,
            token_endpoint: `${origin}/connect/token`,
            end_session_endpoint: `${origin}/connect/logout`,
            userinfo_endpoint: `${origin}/connect/userinfo`,
          },
        }
      : {}),
    client_id: options.clientId,
    redirect_uri: `${appUrl}${callbackPath}`,
    post_logout_redirect_uri: options.postLogoutUrl || appUrl,
    response_type: 'code',
    scope: options.scopes,
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
    loadUserInfo: false,
  })

  const user = shallowRef<AuthUser | null>(null)
  let current: User | null = null
  // Set when a session we had is lost, so the next sign-in says "sua sessão expirou".
  let sessionLost = false

  function setUser(value: User | null) {
    current = value && !value.expired ? value : null
    user.value = current
      ? {
          subject: current.profile.sub,
          name: current.profile.name ?? current.profile.preferred_username,
          email: current.profile.email,
        }
      : null
  }

  manager.events.addUserLoaded(setUser)
  manager.events.addUserUnloaded(() => setUser(null))
  const lose = () => {
    if (current) sessionLost = true
    setUser(null)
  }
  manager.events.addSilentRenewError(lose)
  manager.events.addAccessTokenExpired(lose)

  const auth: Auth = {
    user: readonly(user),
    callbackPath: `/${callbackPath}`,
    isAuthenticated: () => current !== null && !current.expired,
    getAccessToken: async () => (current && !current.expired ? current.access_token : null),
    login: (returnPath, loginOptions) => {
      const extraQueryParams: Record<string, string> = {}
      if (loginOptions?.tenant) extraQueryParams.tenant = loginOptions.tenant
      if (loginOptions?.expired ?? sessionLost) extraQueryParams.expired = '1'
      return manager.signinRedirect({ state: { returnPath: returnPath ?? '/' }, extraQueryParams })
    },
    handleCallback: async () => {
      const signedIn = await manager.signinRedirectCallback()
      sessionLost = false
      setUser(signedIn)
      const state = signedIn.state as { returnPath?: string } | undefined
      return safeReturnPath(state?.returnPath)
    },
    logout: async () => {
      const idToken = current?.id_token
      setUser(null)
      await manager.removeUser()
      await manager.signoutRedirect(idToken ? { id_token_hint: idToken } : undefined)
    },
    install: (app) => {
      app.provide(AUTH_KEY, auth)
    },
  }

  return auth
}

export function useAuth(): Auth {
  const auth = inject(AUTH_KEY)
  if (!auth) {
    throw new Error('useAuth() requires the auth plugin: app.use(createAuth(...)).')
  }
  return auth
}

// Routes are private unless `meta.public` is true; unauthenticated navigation starts sign-in and
// comes back to the requested route afterwards. A route with the tenant param (default
// :tenantId) sends it along, so the login shows that tenant's brand. On a tenant address the
// tenant is fixed (`tenant`) and the routes have no param.
export function installAuthGuard(router: Router, auth: Auth, options: { tenantParam?: string; tenant?: string } = {}): void {
  const tenantParam = options.tenantParam ?? 'tenantId'
  const guard: NavigationGuardWithThis<undefined> = async (to) => {
    if (to.meta.public || auth.isAuthenticated()) {
      return true
    }

    const tenant = to.params[tenantParam]
    await auth.login(to.fullPath, { tenant: typeof tenant === 'string' ? tenant : options.tenant })
    return false
  }

  router.beforeEach(guard)
}

// Only in-app relative paths are accepted as return targets (no open redirect).
export function safeReturnPath(path: string | undefined): string {
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
    return '/'
  }

  return path
}

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
  }
}
